'use client'
import { toast as sonnerToast } from 'sonner'
import { Icon } from '../icon'
import { Typography } from '../typography'
import { cva } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import type { CSSVariableColor } from '../../types'

interface ToastProps {
  id: string | number
  title?: string
  description?: string
  button?: {
    label: string
    onClick: () => void
  }
  showDismiss?: boolean
  color?: 'base' | 'neutral' | 'positive' | 'negative' | 'alert'
  variant?: 'liquid' | 'solid'
  iconColor?: CSSVariableColor
}

function toast(toast: Omit<ToastProps, 'id'>) {
  return sonnerToast.custom((id) => (
    <Toast
      id={id}
      title={toast.title}
      description={toast.description}
      color={toast.color}
      variant={toast.variant}
      button={toast.button}
      showDismiss={toast.showDismiss}
      iconColor={toast.iconColor}
    />
  ))
}

const toastVariants = cva('', {
  variants: {
    variant: {
      solid: '',
      liquid: '',
    },
    color: {
      base: '',
      neutral: '',
      positive: '',
      negative: '',
      alert: '',
    },
  },
  compoundVariants: [
    {
      variant: 'solid',
      color: 'base',
      className: 'bg-base-main text-base-main-100 border-base-main-20 border ',
    },
    {
      variant: 'liquid',
      color: 'base',
      className:
        'bg-base-surface-light text-base-surface-100 border-base-surface-20 border',
    },
    {
      variant: 'solid',
      color: 'neutral',
      className:
        'bg-neutral-main text-neutral-main-100 border-neutral-main-20 border',
    },
    {
      variant: 'liquid',
      color: 'neutral',
      className:
        'bg-neutral-surface-light text-neutral-surface-100 border-neutral-surface-20 border',
    },

    {
      variant: 'solid',
      color: 'positive',
      className:
        'bg-positive-main text-positive-main-100 border-positive-main-20 border',
    },
    {
      variant: 'liquid',
      color: 'positive',
      className:
        'bg-positive-surface-light text-positive-surface-100 border-positive-surface-20 border',
    },

    {
      variant: 'solid',
      color: 'negative',
      className:
        'bg-negative-main text-negative-main-100 border-negative-main-20 border',
    },
    {
      variant: 'liquid',
      color: 'negative',
      className:
        'bg-negative-surface-light text-negative-surface-100 border-negative-surface-20 border',
    },
    {
      variant: 'solid',
      color: 'alert',
      className:
        'bg-alert-main text-alert-main-100 border-alert-main-20 border',
    },
    {
      variant: 'liquid',
      color: 'alert',
      className:
        'bg-alert-surface-light text-alert-surface-100 border-alert-surface-20 border',
    },
  ],
  defaultVariants: {
    variant: 'solid',
    color: 'base',
  },
})

function Toast(props: ToastProps) {
  const {
    title,
    description,
    button,
    id,
    color = 'base',
    iconColor,
    variant = 'solid',
    showDismiss = true,
  } = props

  return (
    <div
      className={cn(
        toastVariants({ variant, color }),
        'flex rounded-sm  shadow-lg  md:w-[336px] items-center py-sm px-md',
      )}
    >
      <div className='flex flex-1 items-center'>
        <div className='w-full'>
          {title && (
            <Typography size='md' weight='regular'>
              {title}
            </Typography>
          )}
          {description && (
            <Typography size='sm' weight='regular'>
              {description}
            </Typography>
          )}
        </div>
      </div>

      {(button || showDismiss) && (
        <Icon
          name='XmarkLine'
          size='sm'
          color={iconColor}
          onClick={() => {
            button?.onClick()
            if (showDismiss) {
              sonnerToast?.dismiss(id)
            }
          }}
        />
      )}
    </div>
  )
}

const toastFunctions = {
  error: (toastProps: Omit<ToastProps, 'id'>) =>
    toast({
      title: toastProps.title,
      description: toastProps.description,
      button: toastProps.button,
      color: 'negative',
      iconColor: 'fill-negative-main-100',
      variant: 'solid',
    }),
  success: (toastProps: Omit<ToastProps, 'id'>) =>
    toast({
      title: toastProps.title,
      description: toastProps.description,
      button: toastProps.button,
      color: 'neutral',
      iconColor: 'fill-white-100',
      variant: 'solid',
    }),
}

export { toastFunctions as toast }
